/**
 * FEAT-016 / S-158 — Document Library view.
 *
 * Per-workspace reference material: upload source documents (file / URL / pasted
 * text), preview the extracted markdown, edit name+tags, re-extract, or delete.
 * Skills reference documents by slug via `library docs: <slug>` in a piece's
 * editorial direction.
 *
 * Mirrors the PublishTargetsView workspace-selection pattern: the selected
 * workspace drives both the list query (agencyclient_id) and the JWT active
 * agencyclient (via useActiveWorkspace().activate) so uploads land in the same
 * workspace the operator is viewing.
 */

import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useApiQuery } from "@/lib/api";
import { useActiveWorkspace, useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import { markdownToHtml } from "@/lib/markdown";
import { SectionHeader } from "@/components/SectionHeader";
import { useSettingsWorkspaces } from "@/lib/useSettingsWorkspaces";

const LIB_WS_KEY = "ccm:library:active_ws";

interface Agencyclient {
  id: number;
  name: string;
}

interface LibraryDocListItem {
  id: number;
  name: string;
  slug: string;
  source_type: string;
  extraction_status: "pending" | "succeeded" | "failed";
  /** S-375 — pieces naming this slug in their editorial direction. Null when
   *  the list was not workspace-scoped, so the API could not compute it. */
  read_by?: number | null;
  extracted_at: string | null;
  word_count: number;
  char_count: number;
  tags: string[];
  created_at: string;
}

interface LibraryDocDetail extends LibraryDocListItem {
  source_url: string | null;
  original_filename: string | null;
  original_mime_type: string | null;
  markdown_content: string | null;
  extractor_version: string | null;
  metadata: Record<string, unknown>;
}

interface ListResponse {
  data: LibraryDocListItem[];
  meta: { total: number };
}

// ============================================================
// Top-level — workspace resolution
// ============================================================

export function LibraryView() {
  const { data: workspaces, isLoading, error } = useSettingsWorkspaces<Agencyclient>();

  const [selectedWsId, setSelectedWsId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(LIB_WS_KEY);
    return raw ? Number(raw) : null;
  });

  useEffect(() => {
    if (selectedWsId != null) {
      try {
        window.sessionStorage.setItem(LIB_WS_KEY, String(selectedWsId));
      } catch {
        /* sessionStorage unavailable — tolerate */
      }
    }
  }, [selectedWsId]);

  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      const stillValid = workspaces.some((w) => w.id === selectedWsId);
      if (!stillValid) setSelectedWsId(workspaces[0].id);
    }
  }, [workspaces, selectedWsId]);

  if (isLoading) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <div className="state-loading">Loading library…</div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <div className="state-error">
            {error instanceof ApiError && error.status === 403
              ? "You don't have permission to view the library."
              : (error.message ?? "Could not load workspaces.")}
          </div>
        </div>
      </main>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <Header />
          <div className="settings-empty">
            <h2>No workspaces yet</h2>
            <p>Create a workspace under Settings → Workspaces before uploading library documents.</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <LibraryBody
      workspaces={workspaces}
      selectedWsId={selectedWsId}
      onSelectWs={setSelectedWsId}
    />
  );
}

function Header() {
  return (
    <>
      <nav className="editor-breadcrumb" aria-label="Breadcrumb">
        <span>Settings</span>
        <span className="editor-breadcrumb-sep">/</span>
        <span>Library</span>
      </nav>
      <SectionHeader
        eyebrow="Settings · Library"
        title="Document library"
        description="Upload source material once; the platform extracts it to markdown and pipeline skills reference it by slug via “library docs: <slug>” in a piece’s editorial direction."
      />
    </>
  );
}

// ============================================================
// Body
// ============================================================

interface BodyProps {
  workspaces: Agencyclient[];
  selectedWsId: number | null;
  onSelectWs: (id: number) => void;
}

function LibraryBody({ workspaces, selectedWsId, onSelectWs }: BodyProps) {
  const qc = useQueryClient();
  const api = useApi();
  const activeWs = useMemo(
    () => workspaces.find((w) => w.id === selectedWsId) ?? workspaces[0],
    [workspaces, selectedWsId]
  );
  const activeWsId = activeWs.id;

  // S-199 — sync the JWT agencyclient_id claim to the displayed workspace on
  // load so reads (RLS scope) and mutations target it.
  useEnsureActiveWorkspace(activeWsId);

  const { activate, error: switchError, clearError } = useActiveWorkspace();
  const [pendingWsId, setPendingWsId] = useState<number | null>(null);

  const { data, isLoading, error } = useApiQuery<ListResponse>(
    ["library-documents", activeWsId],
    `/library-documents?agencyclient_id=${activeWsId}`
  );

  const [openId, setOpenId] = useState<number | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["library-documents", activeWsId] });

  async function handleWorkspaceChange(nextId: number) {
    if (nextId === activeWsId) return;
    clearError();
    setPendingWsId(nextId);
    try {
      await activate(nextId);
      onSelectWs(nextId);
    } catch {
      /* surfaced via switchError */
    } finally {
      setPendingWsId(null);
    }
  }

  const docs = data?.data ?? [];
  const failedDocs = docs.filter((d) => d.extraction_status === "failed");

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <Header />

        <div className="settings-field" style={{ maxWidth: 360 }}>
          <label className="settings-label" htmlFor="lib-ws">Workspace</label>
          <select
            id="lib-ws"
            className="settings-input"
            value={activeWsId}
            disabled={pendingWsId != null}
            onChange={(e) => void handleWorkspaceChange(Number(e.target.value))}
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
          {switchError && (
            <div className="settings-field-error">
              {switchError.message ?? "Could not switch workspace."}
            </div>
          )}
        </div>

        <UploadPanel
          api={api}
          onUploaded={(msg) => {
            setBanner(msg);
            void refresh();
          }}
        />

        {banner && <div className="state-loading" role="status">{banner}</div>}

        {/* S-375 — the index leads with the failed extraction, framed by what
            actually goes wrong rather than by the word "failed": the slug
            resolves to nothing and the piece drafts without it, with no
            error anywhere the writer will see. */}
        {failedDocs.length > 0 && (
          <div className="settings-warn-block">
            <strong>
              {failedDocs.length === 1
                ? "One document failed to extract"
                : `${failedDocs.length} documents failed to extract`}
            </strong>
            <p>
              A piece naming{" "}
              {failedDocs.map((d) => (
                <code key={d.id}>{d.slug}</code>
              )).reduce((acc: React.ReactNode[], el, i) => (
                i === 0 ? [el] : [...acc, ", ", el]
              ), [])}{" "}
              will draft without it and say nothing — the slug resolves to no
              text. Re-extract or replace the source.
            </p>
          </div>
        )}

        {isLoading ? (
          <div className="state-loading">Loading documents…</div>
        ) : error ? (
          <div className="state-error">{error.message ?? "Could not load documents."}</div>
        ) : docs.length === 0 ? (
          <div className="settings-empty">
            <h2>No documents yet</h2>
            <p>Upload a file, fetch a URL, or paste text above to start the library.</p>
          </div>
        ) : (
          <table className="workspaces-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Source</th>
                <th>Status</th>
                <th>Words</th>
                <th>Slug</th>
                <th>Read by</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr
                  key={d.id}
                  className="workspaces-row"
                  style={{ cursor: "pointer" }}
                  onClick={() => setOpenId(d.id)}
                >
                  <td>{d.name}</td>
                  <td>{d.source_type}</td>
                  <td><StatusBadge status={d.extraction_status} /></td>
                  <td>{d.word_count.toLocaleString()}</td>
                  <td><code>{d.slug}</code></td>
                  <td>
                    {/* A document no piece names is doing nothing: the slug
                        resolves to nothing and pieces draft without it,
                        silently. That is the state worth marking. */}
                    {d.read_by == null ? (
                      <span className="lib-readby-unknown">—</span>
                    ) : d.read_by === 0 ? (
                      <span className="lib-readby-none">No pieces</span>
                    ) : (
                      <>{d.read_by} {d.read_by === 1 ? "piece" : "pieces"}</>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId != null && (
        <DocumentDrawer
          api={api}
          docId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => void refresh()}
        />
      )}
    </main>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color =
    status === "succeeded" ? "#1a7f37" : status === "failed" ? "#cf222e" : "#9a6700";
  return (
    <span style={{ color, fontWeight: 600, fontSize: "0.85em" }}>
      {status}
    </span>
  );
}

// ============================================================
// Upload panel — file / url / pasted text
// ============================================================

type UploadMode = "file" | "url" | "text";

function UploadPanel({
  api,
  onUploaded,
}: {
  api: <T,>(path: string, init?: RequestInit) => Promise<T>;
  onUploaded: (msg: string) => void;
}) {
  const [mode, setMode] = useState<UploadMode>("file");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [tags, setTags] = useState("");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");

  function reset() {
    setFile(null);
    setName("");
    setTags("");
    setUrl("");
    setText("");
  }

  async function submit() {
    setErr(null);
    setBusy(true);
    try {
      if (mode === "file") {
        if (!file) throw new Error("Choose a file to upload.");
        const form = new FormData();
        form.append("file", file);
        if (name.trim()) form.append("name", name.trim());
        if (tags.trim()) form.append("tags", tags.trim());
        await api<LibraryDocDetail>("/library-documents", { method: "POST", body: form });
      } else if (mode === "url") {
        if (!url.trim()) throw new Error("Enter a URL.");
        await api<LibraryDocDetail>("/library-documents", {
          method: "POST",
          body: JSON.stringify({
            source_type: "url",
            source_url: url.trim(),
            name: name.trim() || undefined,
            tags: tags.trim() ? tags.split(",").map((t) => t.trim()).filter(Boolean) : undefined,
          }),
        });
      } else {
        if (!name.trim()) throw new Error("Enter a name.");
        if (!text.trim()) throw new Error("Paste some text.");
        await api<LibraryDocDetail>("/library-documents", {
          method: "POST",
          body: JSON.stringify({
            source_type: "pasted_text",
            name: name.trim(),
            markdown_content: text,
            tags: tags.trim() ? tags.split(",").map((t) => t.trim()).filter(Boolean) : undefined,
          }),
        });
      }
      reset();
      onUploaded("Document uploaded.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings-form" style={{ marginBottom: "1.5rem" }}>
      <div className="settings-toggle" style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
        {(["file", "url", "text"] as UploadMode[]).map((m) => (
          <button
            key={m}
            type="button"
            className="settings-input"
            style={{
              width: "auto",
              fontWeight: mode === m ? 700 : 400,
              background: mode === m ? "var(--accent, #0969da)" : undefined,
              color: mode === m ? "#fff" : undefined,
            }}
            onClick={() => setMode(m)}
          >
            {m === "file" ? "Upload file" : m === "url" ? "From URL" : "Paste text"}
          </button>
        ))}
      </div>

      {mode === "file" && (
        <div className="settings-field">
          <label className="settings-label">File (PDF, .docx, .xlsx, .pptx, HTML, text — max 25 MB)</label>
          <input
            type="file"
            className="settings-input"
            accept=".pdf,.docx,.xlsx,.pptx,.html,.htm,.txt,.md"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
      )}

      {mode === "url" && (
        <div className="settings-field">
          <label className="settings-label">Source URL</label>
          <input
            type="url"
            className="settings-input"
            placeholder="https://example.com/report"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
      )}

      {mode === "text" && (
        <div className="settings-field">
          <label className="settings-label">Markdown / text</label>
          <textarea
            className="settings-input"
            rows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      )}

      <div className="settings-field">
        <label className="settings-label">Name {mode === "text" ? "(required)" : "(optional)"}</label>
        <input
          className="settings-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Q4 Earnings Brief"
        />
      </div>

      <div className="settings-field">
        <label className="settings-label">Tags (comma-separated, optional)</label>
        <input
          className="settings-input"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="finance, q4"
        />
      </div>

      {err && <div className="settings-field-error">{err}</div>}

      <button type="button" className="settings-input" style={{ width: "auto" }} disabled={busy} onClick={() => void submit()}>
        {busy ? "Uploading…" : "Upload"}
      </button>
    </section>
  );
}

// ============================================================
// Detail drawer — preview / edit / re-extract / delete
// ============================================================

function DocumentDrawer({
  api,
  docId,
  onClose,
  onChanged,
}: {
  api: <T,>(path: string, init?: RequestInit) => Promise<T>;
  docId: number;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { data: doc, isLoading, error } = useApiQuery<LibraryDocDetail>(
    ["library-document", docId],
    `/library-documents/${docId}`
  );
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (doc) {
      setName(doc.name);
      setTags((doc.tags ?? []).join(", "));
    }
  }, [doc]);

  const previewHtml = useMemo(
    () => markdownToHtml(doc?.markdown_content ?? ""),
    [doc?.markdown_content]
  );

  async function save() {
    setErr(null);
    setBusy(true);
    try {
      await api(`/library-documents/${docId}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: name.trim(),
          tags: tags.trim() ? tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        }),
      });
      await qc.invalidateQueries({ queryKey: ["library-document", docId] });
      onChanged();
      setMsg("Saved.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  async function reExtract() {
    setErr(null);
    setBusy(true);
    try {
      await api(`/library-documents/${docId}/re-extract`, { method: "POST" });
      await qc.invalidateQueries({ queryKey: ["library-document", docId] });
      onChanged();
      setMsg("Re-extraction complete.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Re-extract failed.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm("Delete this document? Pipeline runs already in flight keep their copy.")) return;
    setErr(null);
    setBusy(true);
    try {
      await api(`/library-documents/${docId}`, { method: "DELETE" });
      onChanged();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Delete failed.");
      setBusy(false);
    }
  }

  return (
    <div className="drawer-backdrop" onClick={onClose} style={backdropStyle}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()} style={drawerStyle}>
        <div className="panel-header" style={{ display: "flex", justifyContent: "space-between" }}>
          <h3>Document</h3>
          <button type="button" className="settings-input" style={{ width: "auto" }} onClick={onClose}>Close</button>
        </div>

        {isLoading ? (
          <div className="state-loading">Loading…</div>
        ) : error || !doc ? (
          <div className="state-error">{error?.message ?? "Not found."}</div>
        ) : (
          <>
            <div className="settings-field">
              <label className="settings-label">Name</label>
              <input className="settings-input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="settings-field">
              <label className="settings-label">Tags (comma-separated)</label>
              <input className="settings-input" value={tags} onChange={(e) => setTags(e.target.value)} />
            </div>

            <div className="settings-field">
              <label className="settings-label">Reference snippet</label>
              <code style={{ display: "block", padding: "0.5rem", background: "var(--panel-2, #f6f8fa)" }}>
                library docs: {doc.slug}
              </code>
            </div>

            <dl className="meta-list">
              <div className="meta-row"><dt>Status</dt><dd><StatusBadge status={doc.extraction_status} /></dd></div>
              <div className="meta-row"><dt>Source</dt><dd>{doc.source_type}</dd></div>
              {doc.source_url && <div className="meta-row"><dt>URL</dt><dd>{doc.source_url}</dd></div>}
              {doc.original_filename && <div className="meta-row"><dt>File</dt><dd>{doc.original_filename}</dd></div>}
              <div className="meta-row"><dt>Words</dt><dd>{doc.word_count.toLocaleString()}</dd></div>
              {doc.extractor_version && <div className="meta-row"><dt>Extractor</dt><dd>{doc.extractor_version}</dd></div>}
            </dl>

            {doc.extraction_status === "failed" && (
              <div className="settings-field-error">
                Extraction failed{typeof doc.metadata?.extraction_error === "string" ? `: ${doc.metadata.extraction_error}` : ""}.
                {doc.source_type !== "pasted_text" && " Try re-extract."}
              </div>
            )}

            {err && <div className="settings-field-error">{err}</div>}
            {msg && <div className="state-loading" role="status">{msg}</div>}

            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", margin: "0.75rem 0" }}>
              <button type="button" className="settings-input" style={{ width: "auto" }} disabled={busy} onClick={() => void save()}>Save</button>
              {doc.source_type !== "pasted_text" && (
                <button type="button" className="settings-input" style={{ width: "auto" }} disabled={busy} onClick={() => void reExtract()}>Re-extract</button>
              )}
              <button type="button" className="settings-input" style={{ width: "auto", color: "#cf222e" }} disabled={busy} onClick={() => void remove()}>Delete</button>
            </div>

            <div className="settings-field">
              <label className="settings-label">Extracted markdown</label>
              <div className="panel-md" dangerouslySetInnerHTML={{ __html: previewHtml || "<p><em>No extracted content.</em></p>" }} />
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.3)",
  display: "flex",
  justifyContent: "flex-end",
  zIndex: 50,
};

const drawerStyle: React.CSSProperties = {
  width: "min(640px, 92vw)",
  height: "100%",
  overflowY: "auto",
  background: "var(--panel, #fff)",
  padding: "1.25rem",
  boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
};
